import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  EyeIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  FunnelIcon,
} from '../icons';
import { transcriptionAPI } from '../services/api';
import JobStatusBadge from '../components/transcription/JobStatusBadge';
import CaseReferenceLink from '../components/cases/CaseReferenceLink';
import PageLoadingState from '../components/layout/PageLoadingState';
import KpiStatCard from '../components/layout/KpiStatCard';
import {
  parseTranscriptionRecord,
  formatDuration,
  formatDate,
} from '../utils/transcriptionMeta';
import { normalizeJobStatus } from '../utils/transcriptionStatus';
import { getLanguageLabel } from '../utils/languages';
import { useTheme } from '../contexts/ThemeContext';

const COLUMN_DEFS = [
  { key: 'no', label: 'No.' },
  { key: 'title', label: 'Case title' },
  { key: 'case_ref', label: 'Case ref' },
  { key: 'date', label: 'Date' },
  { key: 'duration', label: 'Duration' },
  { key: 'language', label: 'Language' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Actions' },
];

const DEFAULT_VISIBLE = COLUMN_DEFS.map((c) => c.key);

const STATUS_OPTIONS = [
  { value: 'all', label: 'All status' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

const matchesDateRange = (createdAt, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return true;
  if (!createdAt) return false;
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return false;
  if (dateFrom && d < new Date(`${dateFrom}T00:00:00`)) return false;
  if (dateTo && d > new Date(`${dateTo}T23:59:59`)) return false;
  return true;
};

const Transcriptions = () => {
  const navigate = useNavigate();
  const { dark } = useTheme();
  const [transcriptions, setTranscriptions] = useState([]);
  const [allForKpi, setAllForKpi] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTranscriptions, setTotalTranscriptions] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(false);
  const requestIdRef = useRef(0);
  const [fallbackMode, setFallbackMode] = useState(false);
  const [fallbackResults, setFallbackResults] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    if (!debouncedSearch) {
      setFallbackMode(false);
      setFallbackResults([]);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    transcriptionAPI
      .getTranscriptions({ page: 1, per_page: 500 })
      .then((resp) => {
        const list = resp.data?.data || resp.data || [];
        setAllForKpi(list.map(parseTranscriptionRecord));
      })
      .catch(() => setAllForKpi([]));
  }, []);

  const kpiStats = useMemo(() => {
    const list = allForKpi.length ? allForKpi : transcriptions;
    return {
      total: list.length,
      processing: list.filter((t) => {
        const s = normalizeJobStatus(t.status);
        return s === 'queued' || s === 'processing';
      }).length,
      complete: list.filter((t) => normalizeJobStatus(t.status) === 'complete').length,
      failed: list.filter((t) => normalizeJobStatus(t.status) === 'failed').length,
    };
  }, [allForKpi, transcriptions]);

  const extractTotal = (resp) =>
    resp?.data?.total ?? resp?.data?.pagination?.total ?? resp?.headers?.['x-total-count'] ?? null;

  useEffect(() => {
    if (!fallbackMode) return;
    const filtered = fallbackResults.filter((t) =>
      matchesDateRange(t.created_at, dateFrom, dateTo)
    );
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setTranscriptions(filtered.slice(start, end));
    setTotalTranscriptions(filtered.length);
    setTotalPages(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
    setHasNextPage(end < filtered.length);
  }, [fallbackMode, fallbackResults, currentPage, dateFrom, dateTo, itemsPerPage]);

  useEffect(() => {
    if (fallbackMode) return;
    const fetchTranscriptions = async () => {
      const myId = ++requestIdRef.current;
      try {
        if (pageLoading) setPageLoading(true);
        else setIsFetching(true);

        const params = { page: currentPage, per_page: itemsPerPage };
        if (debouncedSearch) params.search = debouncedSearch;
        if (statusFilter !== 'all') params.status = statusFilter;

        const response = await transcriptionAPI.getTranscriptions(params);
        if (myId !== requestIdRef.current) return;

        const list = response.data?.data || response.data || [];
        let filtered = debouncedSearch
          ? list.filter((x) =>
              [x.title, x.filename, x.name, x.audio_filename]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
                .includes(debouncedSearch.toLowerCase())
            )
          : list;

        if (statusFilter !== 'all') {
          filtered = filtered.filter((x) => {
            const raw = x.status || x.state;
            if (statusFilter === 'queued') return normalizeJobStatus(raw) === 'queued';
            if (statusFilter === 'processing') return normalizeJobStatus(raw) === 'processing';
            return raw === statusFilter;
          });
        }

        filtered = filtered.filter((x) =>
          matchesDateRange(x.created_at, dateFrom, dateTo)
        );

        const processed = filtered.map((x) => parseTranscriptionRecord(x));

        if (debouncedSearch && processed.length === 0) {
          try {
            const allResp = await transcriptionAPI.getTranscriptions({ page: 1, per_page: 1000 });
            const all = allResp.data?.data || allResp.data || [];
            const needle = debouncedSearch.toLowerCase();
            const filteredAll = all
              .filter((x) => {
                const matchesSearch = [x.title, x.filename, x.name, x.audio_filename]
                  .filter(Boolean)
                  .join(' ')
                  .toLowerCase()
                  .includes(needle);
                const matchesStatus =
                  statusFilter === 'all' || (x.status || x.state) === statusFilter;
                const matchesDate = matchesDateRange(x.created_at, dateFrom, dateTo);
                return matchesSearch && matchesStatus && matchesDate;
              })
              .map((x) => parseTranscriptionRecord(x));
            setFallbackMode(true);
            setFallbackResults(filteredAll);
            setIsFetching(false);
            setPageLoading(false);
            return;
          } catch {
            // fall through
          }
        }

        setTranscriptions(processed);
        let total = extractTotal(response);
        if (total == null && !debouncedSearch) {
          try {
            const probe = await transcriptionAPI.getTranscriptions({ page: 1, per_page: 1 });
            total = extractTotal(probe);
          } catch {
            // ignore
          }
        }
        if (total != null) {
          setTotalTranscriptions(total);
          setTotalPages(Math.max(1, Math.ceil(total / itemsPerPage)));
        } else {
          setTotalTranscriptions((currentPage - 1) * itemsPerPage + processed.length);
          setTotalPages(currentPage + (processed.length === itemsPerPage ? 1 : 0));
        }
        setHasNextPage(processed.length === itemsPerPage);
      } catch (e) {
        if (myId !== requestIdRef.current) return;
        setError(`Failed to load transcriptions: ${e.response?.data?.message || e.message}`);
        setTranscriptions([]);
        setTotalTranscriptions(0);
        setTotalPages(0);
        setHasNextPage(false);
      } finally {
        if (myId === requestIdRef.current) {
          setIsFetching(false);
          setPageLoading(false);
        }
      }
    };
    fetchTranscriptions();
  }, [currentPage, debouncedSearch, statusFilter, dateFrom, dateTo, fallbackMode, itemsPerPage]);

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) {
        if (prev.length <= 2) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  const showCol = (key) => visibleColumns.includes(key);
  const activeFilterCount = [statusFilter !== 'all'].filter(Boolean).length;
  const openDetail = (id) => navigate(`/transcriptions/${id}`);

  const inputCls = `px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900'}`;
  const searchCls = `${inputCls} w-full`;
  const dateCls = `${inputCls} w-[150px] shrink-0`;
  const toolBtn = `inline-flex items-center justify-center h-9 px-3 rounded-lg border text-sm ${
    dark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
  }`;
  const dropdownPanel = 'absolute right-0 top-full pt-1 z-20 hidden group-hover:block';
  const dropdownInner = `w-64 p-3 rounded-xl border shadow-lg space-y-3 ${
    dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
  }`;

  const clearFilters = () => {
    setStatusFilter('all');
    setCurrentPage(1);
  };

  const exportCsv = () => {
    const headers = ['Title', 'Case Reference', 'Date', 'Duration (s)', 'Language', 'Status'];
    const lines = transcriptions.map((t) =>
      [
        t.title || '',
        t.case_reference || '',
        t.created_at || '',
        t.duration || '',
        getLanguageLabel(t.language) || '',
        t.status || '',
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',')
    );
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `proceedings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as CSV');
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(transcriptions, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `proceedings-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as JSON');
  };

  if (pageLoading) {
    return <PageLoadingState message="Loading proceedings…" />;
  }

  return (
    <>
      {error && <div className="md-alert md-alert-danger mb-4">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiStatCard label="Total proceedings" value={kpiStats.total} hint="All transcripts in court" icon={DocumentTextIcon} tone="primary" />
        <KpiStatCard label="Processing" value={kpiStats.processing} hint="Currently in pipeline" icon={ArrowPathIcon} tone="warning" />
        <KpiStatCard label="Completed" value={kpiStats.complete} hint="Ready for review" icon={CheckCircleIcon} tone="success" />
        <KpiStatCard label="Failed" value={kpiStats.failed} hint="Needs attention" icon={DocumentTextIcon} tone="danger" />
      </div>

      <div className={`md-card overflow-hidden ${dark ? '!bg-gray-900 !border-gray-800' : ''}`}>
        <div className={`px-4 py-3 border-b flex flex-wrap items-center gap-2 ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <div className="relative w-full max-w-sm min-w-[180px] shrink">
            <MagnifyingGlassIcon className="h-4 w-4 text-matdash-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Search transcripts..."
              className={`${searchCls} pl-9 h-9 py-1.5`}
            />
          </div>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
            className={`${dateCls} h-9 py-1.5`}
            aria-label="From date"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
            className={`${dateCls} h-9 py-1.5`}
            aria-label="To date"
          />

          <div className="ml-auto flex items-center gap-2 shrink-0">
            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Filters">
                <FunnelIcon className="h-4 w-4" />
                {activeFilterCount > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-matdash-primary text-white text-[10px] font-semibold">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <div className={dropdownPanel}>
                <div className={dropdownInner}>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-matdash-muted">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                      className={`${inputCls} w-full`}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <button type="button" onClick={clearFilters} className="text-xs text-matdash-primary">
                    Clear filters
                  </button>
                </div>
              </div>
            </div>

            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Column visibility">
                <Cog6ToothIcon className="h-4 w-4" />
              </button>
              <div className={dropdownPanel}>
                <div className={`w-52 py-2 rounded-xl border shadow-lg ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                  {COLUMN_DEFS.map((col) => (
                    <label
                      key={col.key}
                      className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${
                        dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.key)}
                        onChange={() => toggleColumn(col.key)}
                        className="rounded border-gray-300 text-matdash-primary"
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Export">
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span className="ml-1.5 hidden sm:inline">Export</span>
              </button>
              <div className={dropdownPanel}>
                <div className={`w-44 py-1 rounded-xl border shadow-lg ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <button
                    type="button"
                    onClick={exportCsv}
                    disabled={transcriptions.length === 0}
                    className={`w-full text-left px-3 py-2 text-sm disabled:opacity-50 ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                  >
                    Export as CSV
                  </button>
                  <button
                    type="button"
                    onClick={exportJson}
                    disabled={transcriptions.length === 0}
                    className={`w-full text-left px-3 py-2 text-sm disabled:opacity-50 ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                  >
                    Export as JSON
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={`px-4 py-2 border-b text-xs ${dark ? 'border-gray-800 text-gray-400' : 'border-matdash-border text-gray-500'}`}>
          {fallbackMode
            ? 'Showing expanded search results'
            : `${totalTranscriptions} proceeding${totalTranscriptions !== 1 ? 's' : ''}`}
        </div>

        <div className="relative overflow-x-auto">
          {isFetching && (
            <div className="absolute inset-x-0 top-0 z-10 h-10 bg-white/80 backdrop-blur flex items-center justify-center border-b border-matdash-border">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-matdash-primary" />
              <span className="ml-2 text-xs text-matdash-muted">Updating…</span>
            </div>
          )}

          <table className="md-table min-w-full">
            <thead>
              <tr>
                {showCol('no') && <th className="w-14 text-center">No.</th>}
                {showCol('title') && <th>Case title</th>}
                {showCol('case_ref') && <th>Case ref</th>}
                {showCol('date') && <th>Date</th>}
                {showCol('duration') && <th>Duration</th>}
                {showCol('language') && <th>Language</th>}
                {showCol('status') && <th>Status</th>}
                {showCol('actions') && <th className="text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {transcriptions.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length || 1} className="text-center text-matdash-muted py-10">
                    {searchTerm || statusFilter !== 'all' || dateFrom || dateTo
                      ? 'No transcriptions match your filters.'
                      : 'No transcriptions found. Start by creating one from the dashboard.'}
                  </td>
                </tr>
              )}
              {transcriptions.map((t, index) => (
                <tr
                  key={t.id}
                  onClick={() => openDetail(t.id)}
                  className="cursor-pointer hover:bg-matdash-primary-subtle/30"
                >
                  {showCol('no') && (
                    <td className="text-center">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-matdash-primary-subtle text-matdash-primary text-xs font-semibold">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </span>
                    </td>
                  )}
                  {showCol('title') && (
                    <td className="font-medium min-w-[200px]">{t.title || 'Untitled Transcription'}</td>
                  )}
                  {showCol('case_ref') && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <CaseReferenceLink caseReference={t.case_reference} caseId={t.case_id} />
                    </td>
                  )}
                  {showCol('date') && (
                    <td className="text-matdash-muted whitespace-nowrap">{formatDate(t.created_at)}</td>
                  )}
                  {showCol('duration') && (
                    <td className="text-matdash-muted whitespace-nowrap">{formatDuration(t.duration)}</td>
                  )}
                  {showCol('language') && (
                    <td className="text-matdash-muted">{getLanguageLabel(t.language) || '—'}</td>
                  )}
                  {showCol('status') && (
                    <td><JobStatusBadge status={t.status} /></td>
                  )}
                  {showCol('actions') && (
                    <td className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Link to={`/transcriptions/${t.id}`} className="md-link inline-flex p-1" title="View">
                        <EyeIcon className="h-4 w-4" />
                      </Link>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={`px-4 py-3 border-t flex flex-wrap items-center justify-between gap-3 ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <p className="text-xs text-matdash-muted mb-0">
            Showing {transcriptions.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to{' '}
            {(currentPage - 1) * itemsPerPage + transcriptions.length} of {totalTranscriptions} results
          </p>
          <div className="flex items-center gap-2">
            <select
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className={`h-8 px-2 text-xs rounded-lg border ${dark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200'}`}
            >
              {[10, 25, 50].map((n) => (
                <option key={n} value={n}>{n} / page</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40"
            >
              ‹
            </button>
            <span className="text-xs text-matdash-muted px-1">
              Page {currentPage}{totalPages ? ` of ${totalPages}` : ''}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={!hasNextPage}
              className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40"
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Transcriptions;
